import { Link, generatePath } from "react-router-dom";

import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import { PrintOutlined, RemoveCircleOutline } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { formatNotaryData, truncate } from "../../../common/utils/formatters";

import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { PublicRoutesConfig } from "../../RoutesConfig";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import { Rules } from "../../../common/validationRules";
import withdrawnAuthorizationService from "../../../services/withdrawnAuthorizationService";
import notaryService from "../../../services/notaryService";
import { LoadingButton } from "@mui/lab";
import { AuthorizationSearchDto } from "../../../interfaces/Authorization";
import { PageDefaults } from "../../../common/interfaces/PageDto";
import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { useSearchForm } from "../../../common/hooks/useSearchForm";
import TableSkeleton from "../../../common/components/TableSkeleton";
import { WithdrawnAuthorizationSearchResultDto } from "../../../interfaces/WithdrawnAuthorization";
import usePrint from "../../../common/hooks/usePrint";
import { useState } from "react";
import SearchLogPrint from "../../shared/SearchLogPrint";

const defaultModel: AuthorizationSearchDto = {
  ...PageDefaults,
  authorizer: true,
  authorized: true,
  description: true,
};

export default function Search() {
  const loading = useAxiosLoader(false);
  const { printComponentRef, print } = usePrint();
  const [refNo, setRefNo] = useState<string>();

  const form = useSearchForm<AuthorizationSearchDto, WithdrawnAuthorizationSearchResultDto>({
    defaultModel: defaultModel,
    rootSearchPath: PublicRoutesConfig.WithdrawnAuthorization.Search.Path,
    searchAsync: async (query: string) => {
      await withdrawnAuthorizationService.getWithdrawnAuthorizationsAsync(query).then(async ({ data }) => {
        await withdrawnAuthorizationService.getWithdrawnAuthorizationsCountAsync(query).then(({ data: count }) => {
          form.setResults(data.results, count);
          setRefNo(data.refNo);
        });
      });
    },
  });

  const renderForm = () => {
    return (
      <form onReset={form.onReset} onSubmit={form.onSubmit}>
        <Stack spacing={3}>
          <SectionExtended title="Данни за пълномощно">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SelectExtended
                  label="Нотариус"
                  name={nameof(defaultModel.notaryId)}
                  form={form}
                  searchRequest={notaryService.getNotaries}
                  initRequest={notaryService.getNotariesByIds}
                  rules={Rules.Required}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextFieldExtended
                  label="Регистрационен номер"
                  name={nameof(defaultModel.registrationNumber)}
                  form={form}
                  rules={{ ...Rules.Required, ...Rules.Number }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePickerExtended
                  label="Дата"
                  name={nameof(defaultModel.issueDate)}
                  form={form}
                  rules={{ ...Rules.Required, ...Rules.Date }}
                />
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<RemoveCircleOutline />} type="reset" onClick={() => setRefNo("")}>
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                {refNo && refNo.length > 0 && (
                  <Button startIcon={<PrintOutlined />} onClick={print}>
                    СПРАВКА № {refNo}
                  </Button>
                )}
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={PublicRoutesConfig.Authorization.Search.Icon}
                  type="submit"
                >
                  {PublicRoutesConfig.Authorization.Search.Label}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderTable = () => {
    return loading ? (
      <TableSkeleton />
    ) : form.results && form.results.length > 0 ? (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Рег. №</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Нотариус</TableCell>
                <TableCell>Упълномощител/и</TableCell>
                <TableCell>Упълномощен/и</TableCell>
                <TableCell>Данни за документ</TableCell>
                <TableCell width="90px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.results.map((item) => (
                <TableRow key={item.withdrawnAuthorizationId} hover>
                  <TableCell>{item.registrationNumber}</TableCell>
                  <TableCell>{dateToStringShort(item.issueDate)}</TableCell>
                  <TableCell>{formatNotaryData(item.notary?.name, item.notary?.number)}</TableCell>
                  <TableCell>{truncate(item.authorizer)}</TableCell>
                  <TableCell>{truncate(item.authorized)}</TableCell>
                  <TableCell>{truncate(item.description)}</TableCell>
                  <TableCell>
                    <Stack direction="row" justifyContent="end">
                      <Tooltip title={PublicRoutesConfig.WithdrawnAuthorization.Preview.Label}>
                        <IconButton
                          component={Link}
                          to={generatePath(PublicRoutesConfig.WithdrawnAuthorization.Preview.Path, {
                            id: item.withdrawnAuthorizationId.toString(),
                          })}
                        >
                          {PublicRoutesConfig.WithdrawnAuthorization.Preview.Icon}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={form.pageCount}
          onChange={form.onPageChanged}
          page={form.watch(nameof(defaultModel.currentPage))}
        />
      </>
    ) : (
      form.searched && <AlertExtended type="warning">Няма намерени резултати.</AlertExtended>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Справка за документи, с които се оттегля пълномощно</Typography>
      {renderForm()}
      {renderTable()}
      <SearchLogPrint refNo={refNo} printComponentRef={printComponentRef} results={form.results} isWithdrawn={true} />
    </Stack>
  );
}
